import { useEffect, useState } from "react";
import { Alert, Grid, Typography } from "@mui/material";
import { toast } from "react-toastify";

import { BucketAwtENUM, useUploadFile } from "@/shared";
import { CustomPaperBox } from "@/shared/components/ui";
import { ScrollableDialogProps } from "@/shared/components/ui/Dialogs";
import { useUiStore } from "@/store/ui";
import { OrdenTrabajo } from "@/shared/interfaces";
import FotosVisitaMultiUploader from "./FotosVisitaMultiUploader";
import {
  FOTOS_PROCESO_MAX_VISITA,
  FOTOS_PROCESO_MIN_VISITA,
  eliminadasProcesoVisitaDe,
  fotosProcesoVisitaDe,
  subirFotosVisita,
} from "./fotos-visita.utils";
import {
  agregarFotosTraslado,
  useRefrescarFotosTrasladoLive,
  useSetFotosTrasladoLive,
} from "./traslado/fotos-traslado.actions";

const MIN = FOTOS_PROCESO_MIN_VISITA;
const MAX = FOTOS_PROCESO_MAX_VISITA;

type SubirFotosProcesoModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  ordenTrabajo?: OrdenTrabajo;
};

/**
 * Modal para subir "Fotos de proceso" de la VISITA (mín 2, máx 5 en total). Se
 * pueden subir en cualquier momento. Va contra el endpoint dedicado
 * (`fotos-visita/agregar/`), que primero REPONE las fotos que Activaciones
 * eliminó y luego agrega hasta el máximo, sin pisar lo que haya hecho la otra
 * parte mientras tanto (no usa el PUT genérico de la OT).
 */
export default function SubirFotosProcesoModal({
  open,
  onClose,
  onSuccess,
  ordenTrabajo,
}: SubirFotosProcesoModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [showError, setShowError] = useState(false);
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);
  const { handleUploadFile } = useUploadFile();
  const setFotosLive = useSetFotosTrasladoLive();
  const refrescarFotosLive = useRefrescarFotosTrasladoLive();

  const idOT = ordenTrabajo?.id_orden_trabajo;
  const cargadas = fotosProcesoVisitaDe(ordenTrabajo).length;
  const eliminadas = eliminadasProcesoVisitaDe(ordenTrabajo).length;
  // Cupo: primero se reponen las eliminadas, luego se agrega hasta el máximo.
  const room = eliminadas + Math.max(0, MAX - cargadas - eliminadas);
  const totalTrasSubir = cargadas + files.length;

  const PATH_BUCKET = `${BucketAwtENUM.IMAGE_INSTALACION}/${idOT}`;

  // Si cambia el cupo mientras el modal está abierto (Activaciones eliminó otra
  // foto o ya se completó), se recorta la selección.
  useEffect(() => {
    setFiles((prev) => (prev.length > room ? prev.slice(0, Math.max(0, room)) : prev));
  }, [room]);

  const handleConfirm = async () => {
    if (!idOT) return;
    if (files.length === 0) {
      toast.info("Agregá al menos una foto.");
      return;
    }
    if (totalTrasSubir < MIN) {
      setShowError(true);
      return;
    }
    setIsGlobalLoading(true);
    try {
      const nuevas = await subirFotosVisita(files, PATH_BUCKET, handleUploadFile, "Proceso");
      const fotos = nuevas
        .filter((f) => !!f.photoUrl)
        .map((f) => ({ photoUrl: f.photoUrl as string, photoId: f.photoId, fecha_hora: f.fecha_hora }));
      if (fotos.length !== files.length) {
        toast.error("No se pudo subir alguna de las fotos. Inténtalo de nuevo.");
        return;
      }
      const snapshot = await agregarFotosTraslado(idOT, { etapa: "PROCESO", fotos }, "visita");
      setFotosLive(snapshot);
      toast.success("Fotos de proceso guardadas correctamente");
      setFiles([]);
      setShowError(false);
      onClose();
      onSuccess();
    } catch (e) {
      // El backend ya informó el motivo (toast del cliente HTTP); se refresca el
      // estado por si cambió el cupo.
      console.error("Error subir fotos proceso", e);
      refrescarFotosLive(idOT, "visita");
    } finally {
      setIsGlobalLoading(false);
    }
  };

  return (
    <ScrollableDialogProps
      open={open}
      title="Fotos de proceso"
      minWidth="60%"
      contentNode={
        <CustomPaperBox sxPaper={{ p: 3 }}>
          <Grid item xs={12}>
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              Sube las <b>fotos de proceso</b> (mín {MIN}, máx {MAX} en total). Ya
              cargadas: <b>{cargadas}</b>. Puedes agregar {room} más.
            </Alert>
            {eliminadas > 0 && (
              <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                Activaciones eliminó <b>{eliminadas}</b> {eliminadas === 1 ? "foto" : "fotos"} de proceso:
                las primeras que subas ocuparán su lugar.
              </Alert>
            )}
            <FotosVisitaMultiUploader
              files={files}
              setFiles={setFiles}
              min={Math.max(0, MIN - cargadas)}
              max={room}
              label="Nuevas fotos de proceso"
            />
            {showError && totalTrasSubir < MIN && (
              <Typography color="error" fontWeight={700} sx={{ mt: 1 }}>
                Debe haber al menos {MIN} fotos de proceso en total.
              </Typography>
            )}
          </Grid>
        </CustomPaperBox>
      }
      onClose={onClose}
      onConfirm={handleConfirm}
      confirmTextBtn="Guardar fotos"
      confirmVariantBtn="outlined"
    />
  );
}
